import { Wand2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { useKindMutations } from '@/api/vocab'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, Input, Textarea } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/components/ui/utils'
import {
  Facing,
  Gravity,
  Locomotion,
  RegionRole,
  Semantics,
  SemanticsByDomain,
  Transition,
  type AgeBandId,
  type Domain,
  type FieldDef,
  type KindDef,
  type KindDefCreate,
} from '@/schema'
import { labelOf, suggestColor, useVocabStore, validateNewKindId } from '@/stores/vocabStore'

/**
 * Add or edit one vocabulary kind.
 *
 * The semantics section is a **typed form per domain**, generated from the
 * Zod discriminated union in `schema/vocabulary.ts` (§4.4.2) — an
 * `anchor_kind` shows a stand-offset point picker and a facing select, a
 * `surface` shows a rest point and gravity, a `blocked_word` shows
 * category/severity/match. A raw JSON textarea is explicitly not an
 * acceptable implementation, because the people who add kinds are the
 * people who know the *art*, not the schema.
 *
 * What a kind may map onto is closed (§4.4.5): every option offered below
 * comes from a fixed enum the renderer, navmesh and validator already
 * understand. You can add a kind; you cannot add a role.
 */

export interface KindEditorDialogProps {
  open: boolean
  onOpenChange(open: boolean): void
  domain: Domain
  /** `null` = create a new kind in `domain`. */
  kind: KindDef | null
  /** Pre-fill the id (used by "create kind from proposals"). */
  suggestedId?: string
}

type Draft = {
  id: string
  label: string
  description: string
  ai_hint: string
  color: string
  icon: string
  shortcut: string
  applies_to: string[]
  enabled: boolean
  semantics: Record<string, unknown>
  fields: FieldDef[]
}

const APPLIES = ['stage', 'character', 'object', 'story'] as const
const BANDS: AgeBandId[] = ['3-4', '5-6', '7-8']

/** A sensible empty `semantics` per domain, so a new kind is valid from the first keystroke. */
function blankSemantics(domain: Domain): Record<string, unknown> {
  switch (domain) {
    case 'region_kind':
      return { domain, roles: [], z_order: 20 }
    case 'anchor_kind':
      return { domain, affords: [], stand_offset: [0, 0], facing: 'camera' }
    case 'portal_kind':
      return { domain, transition: 'walk_off', needs_anchor: false }
    case 'path_kind':
      return { domain, locomotion: 'walk' }
    case 'surface':
      return { domain, rest_point_default: [50, 50], gravity: 'down', needs_anchor: false }
    case 'action':
      return { domain, fallback: ['idle'], loop: false }
    case 'affordance':
      return { domain, derived_from: '' }
    case 'tag':
      return { domain, group: 'free' }
    case 'emotion':
      return { domain, clip: 'happy', band_min: '3-4' }
    case 'theme':
      return { domain, beats_hint: '' }
    case 'blocked_word':
      return { domain, category: 'custom', severity: 'warn', match: 'word' }
    case 'clip_modifier':
      return { domain, sets: 'facing', value: 'left' }
    case 'skin_suffix':
    default:
      return { domain }
  }
}

function toDraft(domain: Domain, kind: KindDef | null, suggestedId: string | undefined, color: string): Draft {
  if (kind) {
    return {
      id: kind.id,
      label: labelOf(kind),
      description: kind.description ?? '',
      ai_hint: kind.ai_hint ?? '',
      color: kind.color,
      icon: kind.icon ?? '',
      shortcut: kind.shortcut ?? '',
      applies_to: [...kind.applies_to],
      enabled: kind.enabled,
      semantics: { ...(kind.semantics as unknown as Record<string, unknown>) },
      fields: kind.fields.map((f) => ({ ...f })),
    }
  }
  const id = suggestedId ? (domain === 'blocked_word' || suggestedId.startsWith('x:') ? suggestedId : `x:${suggestedId}`) : domain === 'blocked_word' ? '' : 'x:'
  return {
    id,
    label: suggestedId ? titleCase(suggestedId.replace(/^x:/, '')) : '',
    description: '',
    ai_hint: '',
    color,
    icon: '',
    shortcut: '',
    applies_to: defaultApplies(domain),
    enabled: true,
    semantics: blankSemantics(domain),
    fields: [],
  }
}

function defaultApplies(domain: Domain): string[] {
  switch (domain) {
    case 'region_kind':
    case 'anchor_kind':
    case 'portal_kind':
    case 'path_kind':
      return ['stage']
    case 'action':
    case 'emotion':
    case 'skin_suffix':
    case 'clip_modifier':
      return ['character']
    case 'surface':
      return ['stage', 'object']
    case 'theme':
    case 'blocked_word':
      return ['story']
    default:
      return ['stage', 'character', 'object']
  }
}

function titleCase(s: string): string {
  return s.replace(/[_:]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function KindEditorDialog({ open, onOpenChange, domain, kind, suggestedId }: KindEditorDialogProps) {
  const store = useVocabStore()
  const { create, patch } = useKindMutations()
  const seedColor = useMemo(() => suggestColor(store, domain, suggestedId ?? 'x:new'), [store, domain, suggestedId])
  const [draft, setDraft] = useState<Draft>(() => toDraft(domain, kind, suggestedId, seedColor))
  const [semanticsError, setSemanticsError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setDraft(toDraft(domain, kind, suggestedId, seedColor))
      setSemanticsError(null)
    }
    // `seedColor` changes with the store; re-seeding on every store tick would
    // fight the operator's own colour choice, so it is deliberately left out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, domain, kind, suggestedId])

  const isNew = kind === null
  const idError = isNew ? validateNewKindId(draft.id, domain, store) : null
  const labelError = draft.label.trim() ? null : 'Give it a name people will read.'

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }))
  const setSem = (patchObj: Record<string, unknown>) => setDraft((d) => ({ ...d, semantics: { ...d.semantics, ...patchObj } }))

  const save = () => {
    const semantics = { ...draft.semantics, domain }
    const parsed = Semantics.safeParse(semantics)
    if (!parsed.success) {
      const issue = parsed.error.issues[0]
      setSemanticsError(issue ? `${issue.path.join('.') || 'semantics'}: ${issue.message}` : 'Those settings are not valid.')
      return
    }
    setSemanticsError(null)

    const common = {
      label: { en: draft.label.trim() },
      description: draft.description.trim() || undefined,
      ai_hint: draft.ai_hint.trim() || undefined,
      color: draft.color,
      icon: draft.icon.trim() || undefined,
      shortcut: draft.shortcut.trim().slice(0, 1) || undefined,
      applies_to: draft.applies_to as KindDef['applies_to'],
      semantics: parsed.data,
      fields: draft.fields,
      enabled: draft.enabled,
      deprecated: kind?.deprecated ?? false,
    }

    if (isNew) {
      const body: KindDefCreate = { id: draft.id.trim(), domain, ...common }
      create.mutate(body, { onSuccess: () => onOpenChange(false) })
    } else {
      patch.mutate({ domain, id: kind.id, body: common }, { onSuccess: () => onOpenChange(false) })
    }
  }

  const pending = create.isPending || patch.isPending
  const blocked = Boolean(idError || labelError)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? `Add a ${DOMAIN_NOUN[domain]}` : `Edit ${labelOf({ id: draft.id, label: { en: draft.label } })}`}</DialogTitle>
          <DialogDescription>{DOMAIN_BLURB[domain]}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Id" hint={domain === 'blocked_word' ? 'The word or phrase itself, lowercase.' : 'Ids you add start with x:.'} error={idError ?? undefined}>
              <Input
                value={draft.id}
                disabled={!isNew}
                onChange={(e) => set('id', e.target.value.toLowerCase())}
                aria-label="Kind id"
                aria-invalid={Boolean(idError) || undefined}
                className="font-mono"
              />
            </Field>
            <Field label="Name" error={labelError ?? undefined}>
              <Input value={draft.label} onChange={(e) => set('label', e.target.value)} aria-label="Kind name" aria-invalid={Boolean(labelError) || undefined} />
            </Field>
          </div>

          <Field label="What it is" hint="Shown to people here AND given to Claude in the prompt glossary.">
            <Textarea value={draft.description} onChange={(e) => set('description', e.target.value)} rows={2} aria-label="Description" />
          </Field>

          <Field label="Hint for Claude" hint="One sentence telling the model how to recognise it on a picture.">
            <Input value={draft.ai_hint} onChange={(e) => set('ai_hint', e.target.value)} aria-label="AI hint" />
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Colour">
              <div className="flex items-center gap-2">
                <span className="size-8 shrink-0 rounded-lg border border-line" style={{ background: draft.color }} aria-hidden />
                <Input value={draft.color} onChange={(e) => set('color', e.target.value)} aria-label="Colour" className="font-mono text-xs" />
                <Button variant="ghost" size="icon" aria-label="Suggest a colour" onClick={() => set('color', suggestColor(store, domain, draft.id || 'x:new'))}>
                  <Wand2 aria-hidden />
                </Button>
              </div>
            </Field>
            <Field label="Icon" hint="A lucide icon name.">
              <Input value={draft.icon} onChange={(e) => set('icon', e.target.value)} aria-label="Icon" placeholder="tree-pine" />
            </Field>
            <Field label="Shortcut" hint="One key, unique in this domain.">
              <Input value={draft.shortcut} maxLength={1} onChange={(e) => set('shortcut', e.target.value)} aria-label="Shortcut key" />
            </Field>
          </div>

          <Field label="Where it can be used">
            <CheckGroup
              options={APPLIES.map((a) => ({ value: a, label: titleCase(a) }))}
              value={draft.applies_to}
              onChange={(v) => set('applies_to', v)}
              ariaLabel="Where it can be used"
            />
          </Field>

          {/* ---- typed semantics, one shape per domain (§4.4.2) ---------- */}
          <fieldset className="rounded-xl border border-line p-3">
            <legend className="px-1 text-sm font-semibold text-ink">How the engine treats it</legend>
            <SemanticsForm domain={domain} value={draft.semantics} onChange={setSem} />
            {semanticsError ? (
              <p className="mt-2 text-xs text-danger" role="alert">
                {semanticsError}
              </p>
            ) : null}
          </fieldset>

          <FieldsBuilder fields={draft.fields} onChange={(f) => set('fields', f)} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} loading={pending} disabled={blocked}>
            {isNew ? 'Add it' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ------------------------------------------------------------------------ */
/* Per-domain semantics forms                                               */
/* ------------------------------------------------------------------------ */

interface SemProps {
  value: Record<string, unknown>
  onChange(patch: Record<string, unknown>): void
}

function SemanticsForm({ domain, value, onChange }: SemProps & { domain: Domain }) {
  switch (domain) {
    case 'region_kind':
      return <RegionKindForm value={value} onChange={onChange} />
    case 'anchor_kind':
      return <AnchorKindForm value={value} onChange={onChange} />
    case 'portal_kind':
      return <PortalKindForm value={value} onChange={onChange} />
    case 'path_kind':
      return (
        <EnumField
          label="How a character moves along it"
          options={Locomotion.options}
          value={str(value.locomotion, 'walk')}
          onChange={(v) => onChange({ locomotion: v })}
        />
      )
    case 'surface':
      return <SurfaceForm value={value} onChange={onChange} />
    case 'action':
      return <ActionForm value={value} onChange={onChange} />
    case 'affordance':
      return (
        <Field label="Derived from" hint="Documentation only — which rule produces this affordance.">
          <Input value={str(value.derived_from, '')} onChange={(e) => onChange({ derived_from: e.target.value })} aria-label="Derived from" />
        </Field>
      )
    case 'tag':
      return (
        <EnumField
          label="Group"
          options={['mood', 'time', 'place', 'weather', 'free']}
          value={str(value.group, 'free')}
          onChange={(v) => onChange({ group: v })}
        />
      )
    case 'emotion':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Clip it plays" hint="The action clip a character uses to show this.">
            <Input value={str(value.clip, '')} onChange={(e) => onChange({ clip: e.target.value })} aria-label="Clip" />
          </Field>
          <EnumField label="From which age" options={BANDS} value={str(value.band_min, '3-4')} onChange={(v) => onChange({ band_min: v })} />
        </div>
      )
    case 'theme':
      return <ThemeForm value={value} onChange={onChange} />
    case 'blocked_word':
      return <BlockedWordForm value={value} onChange={onChange} />
    case 'clip_modifier':
      return <ClipModifierForm value={value} onChange={onChange} />
    case 'skin_suffix':
      return <p className="text-sm text-ink-2">A skin suffix has no extra settings — the name is the whole of it.</p>
    default:
      return null
  }
}

function RegionKindForm({ value, onChange }: SemProps) {
  const scaleMode = str((value.scale_ref as Record<string, unknown> | undefined)?.mode, 'none')
  return (
    <div className="flex flex-col gap-3">
      <Field label="What characters may do here" hint="Pick from what the engine understands; a new kind chooses roles, it does not invent them.">
        <CheckGroup
          options={RegionRole.options.map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }))}
          value={arr(value.roles)}
          onChange={(v) => onChange({ roles: v })}
          ariaLabel="Roles"
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Surface" hint="Which surface kind things resting here use.">
          <Input value={str(value.surface, '')} onChange={(e) => onChange({ surface: e.target.value || undefined })} aria-label="Surface" placeholder="floor" />
        </Field>
        <Field label="Drawing order" hint="Higher wins an overlap: obstacle 40 > water 30 > ground 20 > sky 10.">
          <Input type="number" value={num(value.z_order, 20)} onChange={(e) => onChange({ z_order: Number(e.target.value) })} aria-label="Z order" />
        </Field>
      </div>
      <Field label="Needs">
        <CheckGroup
          options={[
            { value: 'baseline_y', label: 'A baseline (for walk-behind)' },
            { value: 'elevation', label: 'An elevation (for platforms)' },
          ]}
          value={arr(value.requires)}
          onChange={(v) => onChange({ requires: v.length ? v : undefined })}
          ariaLabel="Requires"
        />
      </Field>
      <Field label="Affords" hint="Comma separated, from the affordance vocabulary.">
        <Input value={arr(value.affords).join(', ')} onChange={(e) => onChange({ affords: splitList(e.target.value) })} aria-label="Affords" />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <EnumField
          label="How a character's size is scaled"
          options={['none', 'own_y', 'fixed_y']}
          value={scaleMode}
          onChange={(v) => onChange({ scale_ref: v === 'none' ? undefined : v === 'own_y' ? { mode: 'own_y' } : { mode: 'fixed_y', y: 50 } })}
        />
        {scaleMode === 'fixed_y' ? (
          <Field label="At this height (% from the top)">
            <Input
              type="number"
              min={0}
              max={100}
              value={num((value.scale_ref as Record<string, unknown>)?.y, 50)}
              onChange={(e) => onChange({ scale_ref: { mode: 'fixed_y', y: clampPct(Number(e.target.value)) } })}
              aria-label="Fixed scale height"
            />
          </Field>
        ) : null}
      </div>
    </div>
  )
}

function AnchorKindForm({ value, onChange }: SemProps) {
  const offset = pair(value.stand_offset, [0, 0])
  return (
    <div className="flex flex-col gap-3">
      <Field label="What a character can do here" hint="Comma separated: sleep, sit, eat, hide, enter…">
        <Input value={arr(value.affords).join(', ')} onChange={(e) => onChange({ affords: splitList(e.target.value) })} aria-label="Affords" />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Surface">
          <Input value={str(value.surface, '')} onChange={(e) => onChange({ surface: e.target.value || undefined })} aria-label="Surface" placeholder="bed" />
        </Field>
        <Field label="How many at once">
          <Input type="number" min={1} value={num(value.capacity, 1)} onChange={(e) => onChange({ capacity: Math.max(1, Math.round(Number(e.target.value))) })} aria-label="Capacity" />
        </Field>
      </div>
      <Field label="Where a character stands" hint="Offset from the anchor point, in percent of the picture. Negative is left and up.">
        <div className="flex items-center gap-2">
          <Input type="number" step={0.5} value={offset[0]} onChange={(e) => onChange({ stand_offset: [Number(e.target.value), offset[1]] })} aria-label="Stand offset x" />
          <Input type="number" step={0.5} value={offset[1]} onChange={(e) => onChange({ stand_offset: [offset[0], Number(e.target.value)] })} aria-label="Stand offset y" />
        </div>
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <EnumField label="Which way they face" options={Facing.options} value={str(value.facing, 'camera')} onChange={(v) => onChange({ facing: v })} />
        <Field label="Accepts" hint="Object kinds that may rest here, comma separated.">
          <Input value={arr(value.accepts).join(', ')} onChange={(e) => onChange({ accepts: e.target.value.trim() ? splitList(e.target.value) : undefined })} aria-label="Accepts" />
        </Field>
      </div>
      <Toggle
        label="Usually hides part of a character standing behind it"
        checked={Boolean(value.occluder_expected)}
        onChange={(v) => onChange({ occluder_expected: v || undefined })}
      />
    </div>
  )
}

function PortalKindForm({ value, onChange }: SemProps) {
  return (
    <div className="flex flex-col gap-3">
      <EnumField label="How the scene changes" options={Transition.options} value={str(value.transition, 'walk_off')} onChange={(v) => onChange({ transition: v })} />
      <Toggle label="A character must stand at an anchor to use it" checked={Boolean(value.needs_anchor)} onChange={(v) => onChange({ needs_anchor: v })} />
    </div>
  )
}

function SurfaceForm({ value, onChange }: SemProps) {
  const p = pair(value.rest_point_default, [50, 50])
  return (
    <div className="flex flex-col gap-3">
      <Field label="Where an object sits by default" hint="Percent inside the thing's own box: 50 / 100 is bottom-centre.">
        <div className="flex items-center gap-2">
          <Input type="number" min={0} max={100} value={p[0]} onChange={(e) => onChange({ rest_point_default: [clampPct(Number(e.target.value)), p[1]] })} aria-label="Rest point x" />
          <Input type="number" min={0} max={100} value={p[1]} onChange={(e) => onChange({ rest_point_default: [p[0], clampPct(Number(e.target.value))] })} aria-label="Rest point y" />
        </div>
      </Field>
      <EnumField label="Which way things fall" options={Gravity.options} value={str(value.gravity, 'down')} onChange={(v) => onChange({ gravity: v })} />
      <Toggle label="Things need an anchor to rest on it" checked={Boolean(value.needs_anchor)} onChange={(v) => onChange({ needs_anchor: v })} />
    </div>
  )
}

function ActionForm({ value, onChange }: SemProps) {
  return (
    <div className="flex flex-col gap-3">
      <Field label="If the clip is missing, try" hint="In order, comma separated. Ends at idle.">
        <Input value={arr(value.fallback).join(', ')} onChange={(e) => onChange({ fallback: splitList(e.target.value) })} aria-label="Fallback chain" />
      </Field>
      <Toggle label="Loops while it plays" checked={Boolean(value.loop)} onChange={(v) => onChange({ loop: v })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Needs an anchor that affords" hint="Leave empty if it can happen anywhere.">
          <Input
            value={str(value.needs_anchor_affordance, '')}
            onChange={(e) => onChange({ needs_anchor_affordance: e.target.value || undefined })}
            aria-label="Needs anchor affordance"
            placeholder="sleep"
          />
        </Field>
        <Field label="Speed (% of the picture per second)" hint="Only for moving actions: walk 12, run 24, toddle 9.">
          <Input
            type="number"
            step={0.5}
            min={0}
            value={num(value.speed_pct_s, 0)}
            onChange={(e) => {
              const n = Number(e.target.value)
              onChange({ speed_pct_s: n > 0 ? n : undefined })
            }}
            aria-label="Speed"
          />
        </Field>
      </div>
    </div>
  )
}

function ThemeForm({ value, onChange }: SemProps) {
  return (
    <div className="flex flex-col gap-3">
      <Field label="What the beats should do" hint="The sentence Claude is given: wish → tries fail → success, in the theme's own terms.">
        <Textarea value={str(value.beats_hint, '')} onChange={(e) => onChange({ beats_hint: e.target.value })} rows={3} aria-label="Beats hint" />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <EnumField
          label="From which age"
          options={['any', ...BANDS]}
          value={str(value.band_min, 'any')}
          onChange={(v) => onChange({ band_min: v === 'any' ? undefined : v })}
        />
        <Field label="Example prompt">
          <Input value={str(value.example_prompt, '')} onChange={(e) => onChange({ example_prompt: e.target.value || undefined })} aria-label="Example prompt" />
        </Field>
      </div>
    </div>
  )
}

function BlockedWordForm({ value, onChange }: SemProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <EnumField
        label="Category"
        options={['violence', 'fear', 'body', 'adult', 'substances', 'slur', 'imitable', 'custom']}
        value={str(value.category, 'custom')}
        onChange={(v) => onChange({ category: v })}
      />
      <EnumField label="What to do" options={['block', 'warn']} value={str(value.severity, 'warn')} onChange={(v) => onChange({ severity: v })} />
      <EnumField label="How to match" options={['word', 'stem', 'phrase']} value={str(value.match, 'word')} onChange={(v) => onChange({ match: v })} />
    </div>
  )
}

function ClipModifierForm({ value, onChange }: SemProps) {
  const sets = str(value.sets, 'facing')
  const isBool = sets === 'mirrored' || sets === 'deprecated'
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <EnumField
        label="What the suffix sets"
        options={['facing', 'view', 'mirrored', 'deprecated']}
        value={sets}
        onChange={(v) => onChange({ sets: v, value: v === 'mirrored' || v === 'deprecated' ? true : '' })}
      />
      {isBool ? (
        <Toggle label="Value" checked={value.value === true} onChange={(v) => onChange({ value: v })} />
      ) : (
        <Field label="Value">
          <Input value={str(value.value, '')} onChange={(e) => onChange({ value: e.target.value })} aria-label="Modifier value" placeholder={sets === 'view' ? '3q' : 'left'} />
        </Field>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* Extra per-instance fields                                                */
/* ------------------------------------------------------------------------ */

function FieldsBuilder({ fields, onChange }: { fields: FieldDef[]; onChange(next: FieldDef[]): void }) {
  const add = () => onChange([...fields, { key: '', label: '', type: 'text' }])
  const update = (i: number, patch: Partial<FieldDef>) => onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)))
  const remove = (i: number) => onChange(fields.filter((_, idx) => idx !== i))

  return (
    <fieldset className="rounded-xl border border-line p-3">
      <legend className="px-1 text-sm font-semibold text-ink">Extra properties</legend>
      <p className="text-xs text-ink-3">Shown in the Properties panel whenever a feature of this kind is selected.</p>
      <div className="mt-2 flex flex-col gap-2">
        {fields.map((f, i) => (
          <div key={i} className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_8rem_auto]">
            <Field label="Key">
              <Input value={f.key} onChange={(e) => update(i, { key: e.target.value })} aria-label={`Field ${i + 1} key`} className="font-mono text-xs" />
            </Field>
            <Field label="Label">
              <Input value={f.label} onChange={(e) => update(i, { label: e.target.value })} aria-label={`Field ${i + 1} label`} />
            </Field>
            <EnumField
              label="Type"
              options={['text', 'number', 'boolean', 'select', 'asset_ref']}
              value={f.type}
              onChange={(v) => update(i, { type: v as FieldDef['type'] })}
            />
            <Button variant="ghost" size="sm" onClick={() => remove(i)} aria-label={`Remove field ${i + 1}`}>
              Remove
            </Button>
          </div>
        ))}
      </div>
      <Button variant="ghost" size="sm" className="mt-2" onClick={add}>
        Add a property
      </Button>
    </fieldset>
  )
}

/* ------------------------------------------------------------------------ */
/* Small typed inputs                                                       */
/* ------------------------------------------------------------------------ */

function EnumField({ label, options, value, onChange, hint }: { label: string; options: readonly string[]; value: string; onChange(v: string): void; hint?: string }) {
  return (
    <Field label={label} hint={hint}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  )
}

function CheckGroup({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: { value: string; label: string }[]
  value: string[]
  onChange(next: string[]): void
  ariaLabel: string
}) {
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
  return (
    <div role="group" aria-label={ariaLabel} className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value.includes(o.value)
        return (
          <button
            key={o.value}
            type="button"
            role="checkbox"
            aria-checked={on}
            onClick={() => toggle(o.value)}
            className={cn(
              'h-9 rounded-full border px-3 text-sm font-medium transition-colors focus-visible:focus-ring',
              on ? 'border-transparent bg-accent-soft text-ink' : 'border-line bg-paper text-ink-2 hover:bg-paper-2',
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

function Toggle({ label, checked, onChange }: { label: ReactNode; checked: boolean; onChange(v: boolean): void }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="size-4 accent-[var(--color-accent)]" />
      {label}
    </label>
  )
}

const ROLE_LABEL: Record<string, string> = {
  walk: 'Walk on it',
  fly: 'Fly through it',
  swim: 'Swim in it',
  block: 'Blocks the way',
  occlude: 'Draws in front',
  hide: 'Can hide in it',
  backdrop: 'Backdrop only',
  support: 'Things rest on it',
}

const DOMAIN_NOUN: Record<Domain, string> = {
  region_kind: 'region kind',
  anchor_kind: 'anchor kind',
  portal_kind: 'portal kind',
  path_kind: 'path kind',
  surface: 'surface',
  action: 'action',
  affordance: 'affordance',
  tag: 'tag',
  emotion: 'emotion',
  theme: 'story theme',
  blocked_word: 'blocked word',
  skin_suffix: 'skin suffix',
  clip_modifier: 'clip modifier',
}

const DOMAIN_BLURB: Record<Domain, string> = {
  region_kind: 'An area of a picture — where a character may walk, what blocks them, what draws in front.',
  anchor_kind: 'A place a character goes to: a bed to sleep in, a door to leave through, a tree to hide behind.',
  portal_kind: 'A way out of a scene and into the next one.',
  path_kind: 'A route across a picture and how a character travels along it.',
  surface: 'What a thing rests on, and where on itself it touches.',
  action: 'Something a character does, with what to play instead when the clip is missing.',
  affordance: 'A capability derived from kinds and roles. Documentation only.',
  tag: 'A free label for mood, time, place or weather.',
  emotion: 'A feeling a character can show, and the clip that shows it.',
  theme: 'What a story is about, and the beats it should hit. This is the only source of story themes.',
  blocked_word: 'A word or phrase stories must not contain, or should be warned about.',
  skin_suffix: 'A suffix that marks a character folder as a skin of another.',
  clip_modifier: 'A suffix on a clip folder name that sets facing, view, mirroring or age.',
}

/* --------------------------------------------------------------- coercion */

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

function arr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

function pair(v: unknown, fallback: [number, number]): [number, number] {
  if (Array.isArray(v) && v.length === 2 && typeof v[0] === 'number' && typeof v[1] === 'number') return [v[0], v[1]]
  return fallback
}

function splitList(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.min(100, Math.max(0, n))
}

/** Exported for the table's "what does this kind do" summary line. */
export function summariseSemantics(kind: KindDef): string {
  const s = kind.semantics as unknown as Record<string, unknown>
  switch (kind.domain) {
    case 'region_kind':
      return arr(s.roles).map((r) => ROLE_LABEL[r] ?? r).join(', ') || 'no roles'
    case 'anchor_kind':
      return arr(s.affords).join(', ') || 'no affordances'
    case 'portal_kind':
      return `${str(s.transition, 'cut')}${s.needs_anchor ? ' · needs an anchor' : ''}`
    case 'path_kind':
      return str(s.locomotion, 'walk')
    case 'surface':
      return `gravity ${str(s.gravity, 'down')}${s.needs_anchor ? ' · needs an anchor' : ''}`
    case 'action':
      return `falls back to ${arr(s.fallback).join(' → ') || 'idle'}`
    case 'tag':
      return str(s.group, 'free')
    case 'emotion':
      return `clip ${str(s.clip, '—')} · from ${str(s.band_min, '3-4')}`
    case 'theme':
      return str(s.beats_hint, '')
    case 'blocked_word':
      return `${str(s.category, 'custom')} · ${str(s.severity, 'warn')} · ${str(s.match, 'word')}`
    case 'clip_modifier':
      return `sets ${str(s.sets, 'facing')} = ${String(s.value)}`
    default:
      return ''
  }
}

/** Kept so a future domain cannot silently fall through this dialog. */
export const SEMANTICS_DOMAINS = Object.keys(SemanticsByDomain) as Domain[]
